import { getBackendUrl } from "@/lib/environment-url";
import { requestApi } from "@/lib/api-client";
import type { Me, BackendAuth, LogoutOptions, BackendLoginOptions } from "@/lib/types/auth";

function normalizeReturnTo(value: string): string {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    throw new Error("returnTo must not be empty");
  }

  // Allow absolute URLs to support multi-origin logout targets (must be whitelisted server-side)
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }

  // Ensure relative paths always start with '/'
  if (trimmed.startsWith("/")) {
    return trimmed;
  }

  return `/${trimmed}`;
}

function buildReturnUrl(frontendOrigin: string, returnTo?: string): string {
  const defaultReturn = `${frontendOrigin}/analytics`;
  if (!returnTo) {
    return defaultReturn;
  }

  const trimmed = returnTo.trim();
  if (!trimmed) {
    return defaultReturn;
  }

  try {
    const url = new URL(trimmed, frontendOrigin);
    return url.toString();
  } catch {
    const path = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
    return `${frontendOrigin}${path}`;
  }
}

/**
 * Backend authentication utilities
 * Handles login, logout, and identity bootstrap fetching
 */
export const backendAuth: BackendAuth = {
  /**
   * Redirect to backend login flow
   * @param options - Optional return target after successful login
   */
  login(options: BackendLoginOptions): void {
    if (typeof window === "undefined") {
      throw new Error("login can only be called in the browser");
    }

    const backendUrl = getBackendUrl();
    const frontendOrigin = window.location.origin;

    const loginUrl = new URL("/auth/login", backendUrl);

    // return_to is where the user goes after successful login (frontend URL)
    const returnUrl = buildReturnUrl(frontendOrigin, options.returnTo);
    loginUrl.searchParams.set("return_to", returnUrl);

    window.location.href = loginUrl.toString();
  },

  /**
   * Log out user by calling backend logout endpoint
   * Clears session cookie and redirects to login
   */
  async logout(options?: LogoutOptions): Promise<void> {
    if (typeof window === "undefined") {
      throw new Error("logout can only be called in the browser");
    }

    const backendUrl = getBackendUrl();
    const frontendOrigin = window.location.origin;
    const logoutUrl = new URL("/auth/logout", backendUrl);
    let returnTo = `${frontendOrigin}/login`;

    if (options?.returnTo) {
      try {
        const normalized = normalizeReturnTo(options.returnTo);
        returnTo = new URL(normalized, frontendOrigin).toString();
      } catch (error) {
        console.error("[Auth] Invalid returnTo passed to logout:", error);
      }
    }

    logoutUrl.searchParams.set("return_to", returnTo);

    // Trigger top-level navigation so the backend can issue cross-origin redirects
    window.location.assign(logoutUrl.toString());
  },

  async getMe(): Promise<Me> {
    try {
      const result = await requestApi<Me>("GET", "/me");
      if (!result) {
        throw new Error("Expected me data but received none");
      }
      return result;
    } catch (error) {
      console.error("[Auth] Failed to fetch me:", error);
      throw error;
    }
  },
};
